/**
 * Application Insights telemetry for the frontend.
 *
 * Initialises the Application Insights JavaScript SDK with:
 *  - Automatic page-view tracking
 *  - Unhandled exception capture
 *  - AJAX / fetch dependency tracking
 *  - Click analytics (auto-collection)
 *
 * Telemetry is **disabled** when the Application Insights connection string
 * is not set (local dev), so there is zero overhead in development.
 *
 * Usage:
 *   import { appInsights } from "@/config/telemetry";
 *   appInsights?.trackEvent({ name: "MyCustomEvent", properties: { ... } });
 */

import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { getRuntimeConfig, isRuntimeConfigReady } from "./runtime";

/**
 * The Application Insights instance, or `null` when telemetry is disabled.
 *
 * Initialized lazily after runtime config is loaded. Access via
 * getAppInsights() or use the convenience functions below.
 */
let _appInsights: ApplicationInsights | null = null;
let _initialized = false;

/**
 * Initialize Application Insights using the runtime config.
 * Must be called after initRuntimeConfig() has completed.
 */
export function initTelemetry(): void {
  if (_initialized) return;
  _initialized = true;

  if (!isRuntimeConfigReady()) {
    console.warn("Telemetry: runtime config not ready, skipping initialization");
    return;
  }

  const config = getRuntimeConfig();
  const connectionString = config.appInsightsConnectionString;

  if (connectionString) {
    _appInsights = createAppInsights(connectionString);
  }
}

/**
 * Get the Application Insights instance (may be null if telemetry is disabled).
 */
export function getAppInsights(): ApplicationInsights | null {
  return _appInsights;
}

function createAppInsights(connStr: string): ApplicationInsights {
  const ai = new ApplicationInsights({
    config: {
      connectionString: connStr,

      // --- Page & Navigation ---
      enableAutoRouteTracking: true, // SPA page-view tracking

      // --- Performance ---
      maxBatchInterval: 15000, // Flush every 15s (default 15s, explicit)
      maxBatchSizeInBytes: 102400, // 100 KB batch limit
      disableFetchTracking: false, // Track fetch() calls
      enableCorsCorrelation: true, // Correlate cross-origin requests
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,

      // --- Error Tracking ---
      disableExceptionTracking: false,

      // --- Sampling ---
      // 100% in prod/demo, configurable via env if needed later
      samplingPercentage: 100,

      // --- Privacy ---
      disableCookiesUsage: false, // Cookies needed for session tracking
    },
  });

  ai.loadAppInsights();

  // Set cloud role for filtering in Azure Monitor
  ai.addTelemetryInitializer((envelope) => {
    if (envelope.tags) {
      envelope.tags["ai.cloud.role"] = "anthm-frontend";
    }
  });

  return ai;
}

/**
 * Set the authenticated user context so Application Insights can
 * correlate telemetry to specific users.
 *
 * Call this after the user authenticates (in AuthContext).
 * Pass `null` to clear on logout.
 */
export function setTelemetryUser(userId: string | null, accountId?: string): void {
  const ai = getAppInsights();
  if (!ai) return;
  if (userId) {
    ai.setAuthenticatedUserContext(userId, accountId, true);
  } else {
    ai.clearAuthenticatedUserContext();
  }
}

/**
 * Track a custom event with optional properties and measurements.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>,
): void {
  getAppInsights()?.trackEvent({ name, properties, measurements });
}

/**
 * Track an exception (caught error).
 */
export function trackException(error: Error, properties?: Record<string, string>): void {
  getAppInsights()?.trackException({ exception: error, properties });
}
